/*
|-----------------------------------------------------------------------------
| News date parsing and ordering
|-----------------------------------------------------------------------------
|
| A news item is displayable only when its original `published_str` contains
| a valid publication date. `sort_date` is used only as the API-normalized UTC
| equivalent of that valid publication date.
|
| Items with a missing/invalid publication date or a future publication date
| are omitted from Home, Focus, Ticker, Saved, and Most Visited lists.
|
*/

const parseSqlUtcDate = (dateValue) => {
  if (!dateValue) {
    return new Date(NaN);
  }

  const value = String(dateValue).trim();

  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/
  );

  if (!match) {
    return new Date(NaN);
  }

  const [
    ,
    year,
    month,
    day,
    hour,
    minute,
    second,
  ] = match;

  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    )
  );
};

export const parseNewsDate = (dateValue) => {
  if (!dateValue) {
    return new Date(NaN);
  }

  const value = String(dateValue).trim();

  if (!value) {
    return new Date(NaN);
  }

  /*
  |--------------------------------------------------------------------------
  | Browser-supported ISO and RFC dates
  |--------------------------------------------------------------------------
  */
  const nativeDate = new Date(value);

  if (!Number.isNaN(nativeDate.getTime())) {
    return nativeDate;
  }

  /*
  |--------------------------------------------------------------------------
  | RFC fallback with a numeric timezone
  | Example: Mon, 15 Jun 2026 14:41:08 +0000
  |--------------------------------------------------------------------------
  */
  const rfcMatch = value.match(
    /^(?:[A-Za-z]{3}, )?(\d{1,2}) ([A-Za-z]{3}) (\d{4}) (\d{2}):(\d{2}):(\d{2}) ([+-]\d{4})/
  );

  if (!rfcMatch) {
    return new Date(NaN);
  }

  const months = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  const day = rfcMatch[1].padStart(2, "0");
  const month = months[rfcMatch[2]];

  if (!month) {
    return new Date(NaN);
  }

  const year = rfcMatch[3];
  const time =
    `${rfcMatch[4]}:${rfcMatch[5]}:${rfcMatch[6]}`;
  const timezone = rfcMatch[7];
  const timezoneIso =
    `${timezone.slice(0, 3)}:${timezone.slice(3)}`;

  const parsedDate = new Date(
    `${year}-${month}-${day}T${time}${timezoneIso}`
  );

  return Number.isNaN(parsedDate.getTime())
    ? new Date(NaN)
    : parsedDate;
};

const hasOriginalPublicationDate = (item) => {
  const publishedValue = String(
    item?.published_str ?? ""
  ).trim();

  if (!publishedValue) {
    return false;
  }

  const publishedTime =
    parseNewsDate(publishedValue).getTime();

  return Number.isFinite(publishedTime);
};

const getNormalizedPublishedTime = (item) => {
  /*
  |--------------------------------------------------------------------------
  | Never use sort_date when the original publication date is missing
  |--------------------------------------------------------------------------
  |
  | Older API versions could generate sort_date from created_at. That made
  | news without a real publication time appear in the feed. The original
  | published_str must therefore be valid before sort_date can be trusted.
  |
  */
  if (!hasOriginalPublicationDate(item)) {
    return null;
  }

  /*
  |--------------------------------------------------------------------------
  | Prefer API-normalized UTC sort_date
  |--------------------------------------------------------------------------
  */
  const sortDateTime =
    parseSqlUtcDate(item?.sort_date).getTime();

  if (Number.isFinite(sortDateTime)) {
    return sortDateTime;
  }

  /*
  |--------------------------------------------------------------------------
  | Fall back to the valid original publication string
  |--------------------------------------------------------------------------
  */
  const publishedTime =
    parseNewsDate(item?.published_str).getTime();

  return Number.isFinite(publishedTime)
    ? publishedTime
    : null;
};

export const getNewsSortTime = (item) => {
  return getNormalizedPublishedTime(item) ?? 0;
};

export const isNewsPublishedNow = (
  item,
  now = Date.now()
) => {
  const publishedTime =
    getNormalizedPublishedTime(item);

  return (
    publishedTime !== null &&
    publishedTime <= now
  );
};

const getNewsId = (item) => {
  const id = Number(item?.id);

  return Number.isFinite(id)
    ? id
    : 0;
};

const getSortMeta = (item, now) => {
  const publishedTime =
    getNormalizedPublishedTime(item);

  /*
  |--------------------------------------------------------------------------
  | Hide missing, invalid, and future publication dates
  |--------------------------------------------------------------------------
  */
  if (
    publishedTime === null ||
    publishedTime > now
  ) {
    return null;
  }

  return {
    item,
    id: getNewsId(item),
    sortTime: publishedTime,
  };
};

export const sortNewsByPublished = (
  items,
  now = Date.now()
) => {
  return (items || [])
    .map((item) => getSortMeta(item, now))
    .filter(Boolean)
    .sort((a, b) => {
      const timeDelta =
        b.sortTime - a.sortTime;

      if (timeDelta !== 0) {
        return timeDelta;
      }

      return b.id - a.id;
    })
    .map((entry) => entry.item);
};

export const compareNewsByPublished = (a, b) => {
  const aTime = getNormalizedPublishedTime(a);
  const bTime = getNormalizedPublishedTime(b);

  /*
  |--------------------------------------------------------------------------
  | Invalid items are always pushed to the end
  |--------------------------------------------------------------------------
  */
  if (aTime === null && bTime === null) {
    return Number(b?.id || 0) - Number(a?.id || 0);
  }

  if (aTime === null) {
    return 1;
  }

  if (bTime === null) {
    return -1;
  }

  const timeDelta = bTime - aTime;

  if (timeDelta !== 0) {
    return timeDelta;
  }

  return Number(b?.id || 0) - Number(a?.id || 0);
};
